import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { translateApiError } from '../../utils/errorMessages'
import api from '../../services/api'
import EntityHistory from '../../components/EntityHistory'
import ConfirmModal from '../../components/ConfirmModal'
import Icon from '../../components/Icon'
import { Button, Input, Select, FormGroup } from '../../components/ui'
import './Admin.css'

const formatPercentage = (value) => {
  if (value == null) return '-'
  return `${Number(value).toFixed(2)}%`
}

function GipRules() {
  const [searchParams] = useSearchParams()
  const budgetIdFromUrl = searchParams.get('budget_id') || ''
  const stageIdFromUrl = searchParams.get('stage_id') || ''
  const shouldCreateFromUrl = searchParams.get('create') === '1'
  const [budgetFilter, setBudgetFilter] = useState(null)
  const [budgetFilterLoading, setBudgetFilterLoading] = useState(Boolean(budgetIdFromUrl))
  const [grouped, setGrouped] = useState([])
  const [stages, setStages] = useState([])
  const [teamOptions, setTeamOptions] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [searchQuery, setSearchQuery] = useState(stageIdFromUrl)
  const [modal, setModal] = useState(null) // { type: 'add' | 'edit', presetStageId?, presetTeam?, rule?: { id, team, stage_id, employee_name, percentage } }
  const [form, setForm] = useState({ stage_id: '', team: '', employee_name: '', percentage: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const createIntentHandled = useRef(false)
  const resolvedStageIdFromUrl = budgetFilter?.stage_id || stageIdFromUrl

  const employeesInTeam = form.team
    ? employees.filter((e) => (e.teams || []).includes(form.team))
    : []

  useEffect(() => {
    fetchRules()
    fetchStages()
    fetchTeamOptions()
    fetchEmployees()
  }, [])

  useEffect(() => {
    let alive = true

    const fetchBudgetFilter = async () => {
      if (!budgetIdFromUrl) {
        setBudgetFilter(null)
        setBudgetFilterLoading(false)
        return
      }

      try {
        setBudgetFilterLoading(true)
        const res = await api.get(`/company/budgets/${budgetIdFromUrl}`)
        const budget = res.data?.budget || {}
        if (!alive) return
        setBudgetFilter({ stage_id: budget.legacy_stage_id || budget.stage_id || '' })
      } catch (e) {
        console.error('Error resolving budget filter:', e)
        if (alive) setBudgetFilter({ stage_id: stageIdFromUrl })
      } finally {
        if (alive) setBudgetFilterLoading(false)
      }
    }

    fetchBudgetFilter()
    return () => {
      alive = false
    }
  }, [budgetIdFromUrl, stageIdFromUrl])

  useEffect(() => {
    if (resolvedStageIdFromUrl) setSearchQuery(resolvedStageIdFromUrl)
  }, [resolvedStageIdFromUrl])

  useEffect(() => {
    if (!shouldCreateFromUrl || createIntentHandled.current || loading || budgetFilterLoading || modal) return
    createIntentHandled.current = true
    setForm({ stage_id: resolvedStageIdFromUrl, team: '', employee_name: '', percentage: '' })
    setError(null)
    setModal({ type: 'add', presetStageId: resolvedStageIdFromUrl, presetTeam: '' })
  }, [shouldCreateFromUrl, loading, budgetFilterLoading, modal, resolvedStageIdFromUrl])

  const fetchStages = async () => {
    try {
      const res = await api.get('/admin/budget/stages')
      if (res.data?.stages) setStages(res.data.stages)
    } catch (e) {
      console.error('Error fetching stages:', e)
    }
  }

  const fetchTeamOptions = async () => {
    try {
      const res = await api.get('/admin/budget/team-options')
      if (res.data?.team_options) setTeamOptions(res.data.team_options)
    } catch (e) {
      console.error('Error fetching team options:', e)
    }
  }

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/admin/budget/employees-simple')
      if (res.data?.employees) setEmployees(res.data.employees)
    } catch (e) {
      console.error('Error fetching employees:', e)
    }
  }

  const fetchRules = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/budget/gip-rules')
      if (response.data) {
        if (response.data.grouped) setGrouped(response.data.grouped)
        setTotal(response.data.total || 0)
      }
    } catch (error) {
      console.error('Error fetching gip rules:', error)
      alert('Ошибка загрузки данных: ' + translateApiError(error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  const effectiveSearchQuery = searchQuery.trim() || resolvedStageIdFromUrl
  const filteredGrouped = grouped.filter(stageGroup => {
    if (!effectiveSearchQuery.trim()) return true
    const query = effectiveSearchQuery.toLowerCase().trim()
    const stageId = (stageGroup.stage_id || '').toLowerCase()
    return stageId.includes(query)
  })
  const hasActiveFilter = Boolean(effectiveSearchQuery.trim())

  const filteredTotal = filteredGrouped.reduce((sum, stageGroup) => {
    return sum + (stageGroup.teams?.reduce((teamSum, team) => teamSum + (team.employees?.length || 0), 0) || 0)
  }, 0)

  const formatStageLabel = (stage) => {
    const contractNumber = (stage?.contract_number || '').trim()
    const stageType = (stage?.distribution_type || stage?.type || stage?.id || '').trim()
    if (contractNumber && stageType) return `${contractNumber} - ${stageType}`
    return stageType || contractNumber || '-'
  }

  const stagesLabelById = stages.reduce((acc, stage) => {
    acc[String(stage.id)] = formatStageLabel(stage)
    return acc
  }, {})

  const openAdd = (presetStageId = '', presetTeam = '') => {
    setForm({
      stage_id: presetStageId,
      team: presetTeam,
      employee_name: '',
      percentage: ''
    })
    setError(null)
    setModal({ type: 'add', presetStageId, presetTeam })
  }

  const openEdit = (rule, stageId, team) => {
    setForm({
      stage_id: stageId,
      team: team,
      employee_name: rule.employee_name || '',
      percentage: rule.percentage != null ? String(rule.percentage) : ''
    })
    setError(null)
    setModal({ type: 'edit', rule: { ...rule, stage_id: stageId, team } })
  }

  const handleSave = async () => {
    const stage_id = (form.stage_id || '').trim()
    const team = (form.team || '').trim()
    const employee_name = (form.employee_name || '').trim()
    const pct = form.percentage === '' ? null : parseFloat(form.percentage)
    if (!stage_id || !team) {
      setError('Укажите стадию и команду')
      return
    }
    if (pct === null || isNaN(pct) || pct < 0 || pct > 100) {
      setError('Укажите процент от 0 до 100')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = { stage_id, team, employee_name, percentage: pct }
      if (modal.type === 'add') {
        await api.post('/admin/budget/gip-rules', payload)
      } else {
        await api.put(`/admin/budget/gip-rules/${modal.rule.id}`, payload)
      }
      setModal(null)
      await fetchRules()
    } catch (err) {
      setError(translateApiError(err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setDeleteError(null)
      await api.delete(`/admin/budget/gip-rules/${deleteTarget.id}`)
      setDeleteTarget(null)
      setDeleteError(null)
      await fetchRules()
    } catch (err) {
      setDeleteError(translateApiError(err.response?.data?.error || err.message))
    }
  }

  if (loading || budgetFilterLoading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="admin-page">
      <div className="section-card">
        <div className="section-card-header">
          <h2>Правила ГИПов</h2>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по стадии..."
              className="admin-search-input"
            />
            <Button variant="primary" onClick={() => openAdd(searchQuery.trim() || resolvedStageIdFromUrl || '', '')} aria-label="Добавить правило">
              + Добавить правило
            </Button>
          </div>
        </div>
        <p className="admin-subtitle" style={{ marginTop: 0 }}>
          {hasActiveFilter
            ? `Найдено ${filteredTotal} ${filteredTotal === 1 ? 'правило' : filteredTotal < 5 ? 'правила' : 'правил'} (из ${total})`
            : `Всего ${total} ${total === 1 ? 'правило' : total < 5 ? 'правила' : 'правил'}`}
        </p>
        {grouped.length === 0 ? (
          <p style={{ margin: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}>Нет данных. Добавьте первое правило.</p>
        ) : filteredGrouped.length === 0 ? (
          <p style={{ margin: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}>Ничего не найдено</p>
        ) : null}
      </div>

      {filteredGrouped.length > 0 && filteredGrouped.map((stageGroup) => (
        <div key={stageGroup.stage_id} className="section-card">
          <div className="section-card-header">
            <h2>Стадия: {stagesLabelById[String(stageGroup.stage_id)] || stageGroup.stage_id}</h2>
            <Button variant="secondary" onClick={() => openAdd(stageGroup.stage_id)}>
              + Добавить для этой стадии
            </Button>
          </div>
          {(stageGroup.teams || []).map((teamGroup) => (
            <div key={teamGroup.team} style={{ marginBottom: 'var(--spacing-lg)' }}>
              <h3 style={{ marginBottom: 'var(--spacing-sm)', fontSize: '16px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                Команда: {teamGroup.team}
              </h3>
              <div className="admin-table-frame">
                <div className="table-responsive">
                  <table className="ui-table data-table">
                    <thead>
                      <tr>
                        <th>Сотрудник</th>
                        <th>Процент</th>
                        <th style={{ width: '100px' }}>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(teamGroup.employees || []).map((emp) => (
                        <tr key={emp.id}>
                          <td>{emp.employee_name || '—'}</td>
                          <td>{formatPercentage(emp.percentage)}</td>
                          <td>
                            <Button variant="ghost" className="icon-button" onClick={() => openEdit(emp, stageGroup.stage_id, teamGroup.team)} title="Изменить" aria-label="Изменить">
                              <Icon name="edit" size={16} />
                            </Button>
                            <Button variant="ghost" className="icon-button" onClick={() => setDeleteTarget({ id: emp.id, stage_id: stageGroup.stage_id, team: teamGroup.team, employee_name: emp.employee_name })} title="Удалить" aria-label="Удалить">
                              <Icon name="trash" size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <Button variant="ghost" onClick={() => openAdd(stageGroup.stage_id, teamGroup.team)} style={{ marginTop: 'var(--spacing-xs)' }}>
                + Добавить в команду {teamGroup.team}
              </Button>
            </div>
          ))}
        </div>
      ))}

      <EntityHistory entityType="gip_rules" title="История изменений (правила ГИПов)" />

      {modal && (modal.type === 'add' || modal.type === 'edit') && (
        <div className="modal-overlay" onClick={() => !saving && setModal(null)}>
          <div className="modal-content" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <h3>{modal.type === 'add' ? 'Добавить правило ГИПов' : 'Изменить правило'}</h3>
            {error && <p className="modal-error" style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-md)' }}>{error}</p>}
            <FormGroup label="Стадия">
              <Select
                value={form.stage_id}
                onChange={(e) => setForm(f => ({ ...f, stage_id: e.target.value }))}
              >
                <option value="">— не выбрана —</option>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>{formatStageLabel(s)}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Команда">
              <Select
                value={form.team}
                onChange={(e) => setForm(f => ({ ...f, team: e.target.value, employee_name: '' }))}
              >
                <option value="">— не выбрана —</option>
                {teamOptions.map((o) => (
                  <option key={o.value || o.label} value={o.value || o.label}>{o.label || o.value}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Сотрудник">
              <Select
                value={form.employee_name}
                onChange={(e) => setForm(f => ({ ...f, employee_name: e.target.value }))}
              >
                <option value="">— не выбран —</option>
                {employeesInTeam.map((e) => (
                  <option key={e.id} value={e.full_name || ''}>{e.full_name || e.id}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Процент (%)">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.percentage}
                onChange={(e) => setForm(f => ({ ...f, percentage: e.target.value }))}
                placeholder="0–100"
              />
            </FormGroup>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => !saving && setModal(null)} disabled={saving}>Отмена</Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Удалить правило ГИПов?"
          message={
            <p>
              Правило для стадии <strong>{stagesLabelById[String(deleteTarget.stage_id)] || deleteTarget.stage_id}</strong>, команда <strong>{deleteTarget.team}</strong>
              {deleteTarget.employee_name ? <>, сотрудник <strong>{deleteTarget.employee_name}</strong></> : null} будет удалено.
            </p>
          }
          confirmLabel="Удалить"
          confirmVariant="danger"
          onConfirm={handleDelete}
          onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
          error={deleteError}
        />
      )}
    </div>
  )
}

export default GipRules
